/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map, Observable, Subject } from 'rxjs';
import { Ticket, TicketType } from '@touchplan/common';
import { ITimelineTicketOverflow, TimelineTicketService } from '../services/timeline-ticket.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PlanAccessCheck } from '../../../services/PlanAccessCheck';
import { FeatureFlagManager } from '../../../services/FeatureFlagManager';
import { CameraService } from '../services/camera.service';
import {
	TIMELINE_TICKETS_CURRENT_PLAN_RESTRICTED,
	TIMELINE_TICKETS_ONLY_ADMINS_CAN_MODIFY_ACCESS,
	TIMELINE_TICKETS_PLAN_TICKET_RESTRICTED,
} from '@touchplan/strings';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { PlanState } from '../../../services/plan-state.service';
import { Point } from 'pixi.js-legacy';
import { CONTEXT_MENUS, ContextMenuService } from '../../ticket-edit/context-menu.service';
import { DependencyLinkageType } from '../../../app-data/models/DependencyLinkageType';
import { cssVar } from '@touchplan/css-var';

const settings = {
	dependencyXOffset: 20, // X offset for the dependency ticket edit panel
	dependencyYOffset: 20, // Y offset for the dependency ticket edit panel
};

/**
 * Interface representing a ticket with computed display properties for the overflow panel.
 * This transforms timeline ticket data into a view-only model containing various states
 */
interface ITimelineTicketDisplay {
	/**
	 * The original ticket data
	 */
	ticket: Ticket;
	/**
	 * CSS class name of the ticket, all values come from the old AngularJS component
	 */
	backgroundClass: string;
	/**
	 * Helper to check if this is a constraint type ticket
	 */
	isConstraint: boolean;
	/**
	 * Helper to check if this is a milestone type ticket
	 */
	isMilestone: boolean;
	/**
	 * If this ticket is currently selected
	 */
	isSelected: boolean;
	/**
	 * Helper for thje activity name for tihis timeline ticket
	 */
	activityName: string;
	/**
	 * Helper for if this timeline ticket exists in a different plan (and thus additional UI should show)
	 */
	isDifferentPlan: boolean;
}
/**
 * Interface for the transformed view model used by the template.
 */
interface ITimelineOverflowViewModel {
	visible: boolean;
	position: { x: number; y: number };
	displayTickets: ITimelineTicketDisplay[];
}
/**
 * TimelineTicketOverflowComponent displays a floating panel of timeline tickets.
 * This component is triggered when users click on timeline ticket indicators,
 * showing detailed information about the tickets and allowing navigation to
 * different plans if the tickets span multiple plans.
 */
@Component({
	selector: 'app-timeline-ticket-overflow',
	standalone: true,
	imports: [CommonModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: `./timeline-ticket-overflow.component.html`,
})
export class TimelineTicketOverflowComponent implements OnDestroy, OnInit {
	/**
	 * Reactive stream of view model data for the template.
	 * This observable transforms service data into template only model.
	 */
	public overflowViewModel$: Observable<ITimelineOverflowViewModel>;
	/**
	 * Subject used to signal component destruction for subscription cleanup.
	 * @private
	 */
	private _shutdownSubject$: Subject<void> = new Subject();
	/**
	 * Current plan ID extracted from route parameters.
	 * Used to determine if tickets belong to different plans.
	 * @private
	 */
	private _planId: string;
	/**
	 * Current project ID extracted from route parameters.
	 * @private
	 */
	private _projectId: string;
	/**
	 * Creates an instance of TimelineTicketOverflowComponent.
	 * @param timelineTicketService Service providing overflow data and control methods
	 * @param route Activated route for accessing route parameters
	 * @param router Router for programmatic navigation
	 * @param planAccessCheck plan access check service
	 * @param featureFlagManager feature flag manager for...feature flags
	 * @param cameraService service to access the canvas camera
	 * @param planState access to the TicketList so we can check if we are in edit mode
	 */
	constructor(
		private timelineTicketService: TimelineTicketService,
		private route: ActivatedRoute,
		private router: Router,
		private planAccessCheck: PlanAccessCheck,
		private featureFlagManager: FeatureFlagManager,
		private cameraService: CameraService,
		private planState: PlanState,
		private contextMenuService: ContextMenuService
	) {}
	/**
	 * Component initialization lifecycle hook.
	 * Sets up the reactive data stream and extracts route parameters.
	 */
	public ngOnInit(): void {
		this.overflowViewModel$ = this.timelineTicketService.overflow$.pipe(
			map((overflowData: ITimelineTicketOverflow) => this.transformToViewModel(overflowData))
		);

		this._projectId = this.route.snapshot.paramMap.get('projectId');
	}
	/**
	 * Component destruction lifecycle hook.
	 */
	public ngOnDestroy(): void {
		this._shutdownSubject$.next();
	}
	/**
	 * TrackBy function for Angular's ngFor directive.
	 * Optimizes rendering performance by providing unique identifiers for list items.
	 * @param index The index of the item in the array
	 * @param item The ticket display item
	 * @returns Unique identifier for the item
	 */
	public trackByTicketId(index: number, item: ITimelineTicketDisplay): string {
		return item.ticket.$id || index.toString();
	}

	/**
	 * Function that is invoked when a display ticket is clicked in the UI.
	 * This should process all of the dependency/feature flag logic.
	 * This is the same logic as the old DOM-based timeline ticket overflow UI
	 * @param ticket
	 */
	public processTimelineTicketClicked(displayTicket: ITimelineTicketDisplay): void {
		const ticket: Ticket = displayTicket.ticket;
		const hasRestrictedAccess: boolean = this.planAccessCheck.hasRestrictedAccessSync;
		const hasAccessToTicket: boolean = this.planAccessCheck.hasAccessToTicketSync(ticket);
		if (hasRestrictedAccess || !hasAccessToTicket) {
			const restrictedMessage: string = hasRestrictedAccess
				? TIMELINE_TICKETS_PLAN_TICKET_RESTRICTED
				: TIMELINE_TICKETS_CURRENT_PLAN_RESTRICTED;
			Logging.warning(`${restrictedMessage}. ${TIMELINE_TICKETS_ONLY_ADMINS_CAN_MODIFY_ACCESS}.`);
			return;
		}

		if (this.featureFlagManager.getFlag(DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG)) {
			const editTickets: Map<string, Ticket> = this.planState.tickets.editTicket$.getValue();

			if (editTickets.size === 1) {
				const predTimelineTicket: Ticket = this.planState.dependencies.checkTicketCache(ticket.$id);
				const worldX: number = ticket.view.isActive
					? ticket.timelineX
					: ticket.getPullColumnXFromDate(ticket.view.liveFinish);
				const screenY: number =
					cssVar.headerHeight_px + cssVar.timelineHeight_px + settings.dependencyYOffset;
				// ContextMenuService accepts coordinates in _world_ space and not screen space
				// We have the world space X coordinate of the timeline ticket, however we do not have a world space Y
				// so we calculate our screen space for the Y and then convert that to the world Y before passing that to the
				// context menu service
				const menuPoint: Point = new Point(worldX, this.cameraService.convertScreenToWorldY(screenY));
				this.contextMenuService.show(CONTEXT_MENUS.TICKET_EDIT_CONFIRM_MENU, menuPoint, {
					tickets: predTimelineTicket,
					// Context Menu Service blows up if we don't pass in an object with a `clear` key that has a function as a value
					// so we pass in a NOOP here instead.
					timelineGroupMenu: { e: ticket, clear: () => {} },
				});
			}
			return;
		}

		this.planState.actions.simpleDependencyToggle(ticket.$id, DependencyLinkageType.StartToFinish);
	}

	/**
	 * Transforms raw timeline ticket data from the service into a view model for the template.
	 * @param overflowData Raw overflow data from the timeline ticket service
	 * @returns Transformed view model
	 * @private
	 */
	private transformToViewModel(overflowData: ITimelineTicketOverflow): ITimelineOverflowViewModel {
		return {
			visible: overflowData?.visible || false,
			position: {
				x: overflowData?.screenPosition?.x || 0,
				y: overflowData?.screenPosition?.y || 0,
			},
			displayTickets: this.createDisplayTickets(overflowData),
		};
	}
	/**
	 * Processes raw tickets into display objects with pre-calculated properties.
	 * @param overflowData timeline ticket data containing tickets to process
	 * @returns Array of processed display tickets
	 * @private
	 */
	private createDisplayTickets(overflowData: ITimelineTicketOverflow): ITimelineTicketDisplay[] {
		if (!overflowData?.visible || !overflowData.tickets) {
			return [];
		}
		this._planId = this.route.snapshot.paramMap.get('planId');
		return overflowData.tickets.map((ticket) => ({
			ticket,
			backgroundClass: this.computeBackgroundClass(ticket),
			isConstraint: this.isConstraint(ticket),
			isMilestone: this.isMilestone(ticket),
			isSelected: this.isSelected(ticket),
			activityName: this.getActivityName(ticket),
			isDifferentPlan: this.isDifferentPlan(ticket),
		}));
	}
	/**
	 * Gets the CSS background class for a ticket based on its role.
	 * @param ticket The ticket
	 * @returns CSS class name based on the ticket's role
	 * @private
	 */
	private computeBackgroundClass(ticket: Ticket): string {
		return ticket.view?.liveRole?.view?.roleId;
	}
	/**
	 * Determines if a ticket is a constraint type.
	 * @param ticket The ticket to check
	 * @returns True if the ticket is a constraint type
	 * @private
	 */
	private isConstraint(ticket: Ticket): boolean {
		return ticket.view.type === TicketType.Constraint;
	}
	/**
	 * Determines if a ticket is a milestone type.
	 * @param ticket The ticket to check
	 * @returns True if the ticket is a milestone type
	 * @private
	 */
	private isMilestone(ticket: Ticket): boolean {
		return ticket.view.type === TicketType.Milestone;
	}
	/**
	 * Determines if a ticket is currently selected.
	 * @param ticket The ticket to check
	 * @returns True if the ticket is selected
	 * @private
	 */
	private isSelected(ticket: Ticket): boolean {
		return ticket.selected;
	}
	/**
	 * Gets the activity name for display.
	 * @param ticket The ticket to extract the activity name from
	 * @returns The activity name or empty string as fallback
	 * @private
	 */
	private getActivityName(ticket: Ticket): string {
		return ticket.view.activityName || '';
	}
	/**
	 * Determines if a ticket belongs to a different plan than the current one.
	 * This is used to show cross-plan navigation indicators and enable plan switching.
	 * @param ticket The ticket to check for cross-plan origin
	 * @returns True if the ticket originates from a different plan
	 * @private
	 */
	private isDifferentPlan(ticket: Ticket): boolean {
		if (!ticket.timelineTicketSplitId || !ticket.timelineTicketSplitId.length) {
			return false;
		}
		return ticket.timelineTicketSplitId[0] !== this._planId;
	}
	/**
	 * Navigates to a different plan within the same project.
	 * @param planId The ID of the plan to navigate to
	 */
	public navigateToPlan(planId: string): void {
		this.router.navigate([`plan`, this._projectId, planId]);
	}
}
